import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Alert } from 'reactstrap';
import Spinner from '../../components/spinner';
import HttpError from '../../components/http-error';
import withAPI from '../../hocs/with-api';
import '../../date-picker.css';
import 'react-datepicker/dist/react-datepicker.css';
import Table from '../../components/table';
import ReportDriversScoreCardRow from '../../components/report-drivers-score-card-row';
import ActionCard from '../../components/action-card';
import DropdownFilter from '../../components/dropdown-filter';
import ButtonWithIcon from '../../components/button-with-icon';
import { scoreCardScoresStatuses } from '../orders/utils';

const MAX_DATE_RANGE_MONTHS = 2;

class ReportDriversScoreCard extends PureComponent {
  constructor() {
    super();
    const endDate = moment().toDate();
    const startDate = moment().subtract(6, 'days').toDate();

    this.state = {
      reportData: [],
      startDate,
      endDate,
      isLoading: false,
      hasError: false,
      hasData: false,
      dateRangeError: null,
      carrierId: null,
      isExporting: false,
      carriers: [],
      sortBy: null,
    };
  }

  async componentDidMount() {
    const { t } = this.props;
    document.title = `${t('reports.driversScoreCard')} - ${t('app.name')}`;
    this.loadCarriers();
    const { startDate, endDate } = this.state;
    if (startDate && endDate) {
      this.loadReportData();
    }
  }

  getMaxEndDate = (startDate) => {
    if (!startDate) return moment().toDate();
    const maxEndDate = moment(startDate).add(MAX_DATE_RANGE_MONTHS, 'months');
    const today = moment();
    return maxEndDate.isBefore(today) ? maxEndDate.toDate() : today.toDate();
  };

  isDateRangeValid = (startDate, endDate) => {
    if (!startDate || !endDate) return true;
    const diffInMonths = moment(endDate).diff(moment(startDate), 'months', true);
    return diffInMonths <= MAX_DATE_RANGE_MONTHS;
  };

  getDateRangeErrorMessage = () => {
    const { t } = this.props;
    return t('reports.maxDateRangeNote', `Date range cannot exceed ${MAX_DATE_RANGE_MONTHS} months`,
      { maxMonths: MAX_DATE_RANGE_MONTHS });
  };

  loadReportData = async () => {
    const { httpRequest } = this.props;
    const { startDate, endDate, carrierId } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    if (!this.isDateRangeValid(startDate, endDate)) {
      this.setState({
        dateRangeError: this.getDateRangeErrorMessage(),
        hasData: false,
      });
      return;
    }

    const data = {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
    };

    if (carrierId) {
      data.carrier_id = carrierId;
    }

    this.setState({
      isLoading: true,
      hasError: false,
      dateRangeError: null,
    });

    try {
      const response = await httpRequest({
        method: 'post',
        url: 'drivers/all-score-card',
        data,
      });

      if (response) {
        const { data: reportData } = response;
        this.setState({
          reportData,
          isLoading: false,
          hasError: false,
          hasData: true,
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      this.setState({
        isLoading: false,
        hasError: true,
        hasData: false,
      });
    }
  };

  onExportClick = async () => {
    const { httpRequest } = this.props;
    const { startDate, endDate, carrierId } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    const data = {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      export: true,
    };

    if (carrierId) {
      data.carrier_id = carrierId;
    }

    this.setState({ isExporting: true });

    try {
      const response = await httpRequest({
        method: 'post',
        url: 'drivers/all-score-card',
        data,
        responseType: 'blob',
      });

      if (response) {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'text/csv',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // eslint-disable-next-line max-len
        const filename = `drivers-scorecard-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.csv`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export Error:', error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  handleSetStartDate = (date) => {
    const { endDate } = this.state;
    let newEndDate = endDate;
    if (endDate && date && (!this.isDateRangeValid(date, endDate) || date > endDate)) {
      newEndDate = null;
    }

    this.setState(
      {
        startDate: date,
        endDate: newEndDate,
        dateRangeError: null,
      },
      () => {
        if (date && newEndDate) {
          this.loadReportData();
        } else {
          this.setState({ hasData: false, reportData: [] });
        }
      },
    );
  };

  handleSetEndDate = (date) => {
    const { startDate } = this.state;

    if (startDate && date && !this.isDateRangeValid(startDate, date)) {
      this.setState({
        dateRangeError: this.getDateRangeErrorMessage(),
      });
      return;
    }

    this.setState(
      {
        endDate: date,
        dateRangeError: null,
      },
      () => {
        if (startDate && date) {
          this.loadReportData();
        }
      },
    );
  };

  onCarrierSelected = (carrierId) => {
    this.setState(
      {
        carrierId,
      },
      () => {
        const { startDate, endDate } = this.state;
        if (startDate && endDate) {
          this.loadReportData();
        }
      },
    );
  };

  handleDrivingScoreSort = () => {
    const { sortBy } = this.state;
    let newSortBy;

    if (sortBy === null) {
      newSortBy = 'desc';
    } else if (sortBy === 'desc') {
      newSortBy = 'asc';
    } else {
      newSortBy = null;
    }

    this.setState({ sortBy: newSortBy });
  };

  getSortedReportData = () => {
    const { reportData, sortBy } = this.state;

    if (!sortBy) {
      return reportData;
    }

    return _.orderBy(reportData, ['driving_score'], [sortBy]);
  };

  getSortIcon = () => {
    const { sortBy } = this.state;

    if (sortBy === 'asc') {
      return '↑';
    } if (sortBy === 'desc') {
      return '↓';
    }
    return '↕';
  };

  async loadCarriers() {
    const { httpRequest } = this.props;
    const { data: carriers } = await httpRequest({
      method: 'get',
      url: 'carriers',
      tag: 'carriers',
    });
    if (carriers) {
      this.setState({
        carriers,
      });
    }
  }

  render() {
    const { t } = this.props;
    const {
      startDate,
      endDate,
      isLoading,
      hasError,
      hasData,
      dateRangeError,
      isExporting,
      carriers,
      carrierId,
    } = this.state;

    const sortedReportData = this.getSortedReportData();

    const filterOptions = _.map(carriers, (carrier) => ({
      id: carrier.id,
      label: carrier.name,
      value: carrier.id,
    }));

    const carrierFilter = (
      <div className="inline-block">
        <DropdownFilter
          dropDownStyles={{ toggleStyles: { maxWidth: '240px' } }}
          options={filterOptions}
          label={t('carriers.one')}
          onSelected={this.onCarrierSelected}
          selectedValue={carrierId}
        />
      </div>
    );

    const scoreLegend = {
      green: `${t('scoreCard.green')}`,
      yellow: `${t('scoreCard.yellow')}`,
      red: `${t('scoreCard.red')}`,
    };

    return (
      <>
        <ActionCard
          title={t('reports.driversScoreCardTitle')}
          withActionButton={false}
          headerChildren={
          [
            <div className="inline-block mr-2 mb-2">
              {carrierFilter}
            </div>,
            <div className="inline-block mr-2 mb-2">
              <DatePicker
                onChange={this.handleSetStartDate}
                selected={startDate}
                className="input-clean center-text kpi-date-picker"
                dateFormat="dd/MM/yyyy"
                maxDate={moment().toDate()}
                placeholderText={t('reports.selectStartDate')}
              />
            </div>,
            <div className="inline-block mb-2">
              <DatePicker
                onChange={this.handleSetEndDate}
                selected={endDate}
                className="input-clean center-text kpi-date-picker"
                dateFormat="dd/MM/yyyy"
                minDate={startDate}
                maxDate={this.getMaxEndDate(startDate)}
                placeholderText={t('reports.selectEndDate')}
              />
            </div>,
            <ButtonWithIcon
              disabled={_.isEmpty(sortedReportData) || isExporting}
              onClick={this.onExportClick}
              icon="/assets/save-icon.png"
              title={t('kpi.export')}
            />,
          ]
        }
        >
          <>
            {dateRangeError && (
            <Alert color="warning" className="mb-3">
              {dateRangeError}
            </Alert>
            )}

            {(!startDate || !endDate) && !dateRangeError && (
            <div>
              <p>{t('reports.selectBothStartAndEndDatesToViewScorecard')}</p>
              <small className="text-muted">
                {t('reports.maxDateRangeNote', `Note: Maximum date range is ${MAX_DATE_RANGE_MONTHS} months`,
                  { maxMonths: MAX_DATE_RANGE_MONTHS })}
              </small>
            </div>
            )}

            {startDate && endDate && !dateRangeError && (
              <>
                {isLoading && <Spinner />}
                {hasError && (
                <HttpError withTryAgainButton onTryAgain={this.loadReportData} />
                )}
                {!isLoading && !hasError && hasData && (
                  <>
                    <div className="mb-3">
                      {_.keys(scoreLegend).map((legendItem) => (
                        <div className="d-flex align-items-center mb-1" key={legendItem}>
                          <div
                            className={`loading-time-status-icon mr-2 ${scoreCardScoresStatuses[legendItem]}`}
                          />
                          <div>{scoreLegend[legendItem]}</div>
                        </div>
                      ))}
                    </div>
                    <Table
                      hover
                      responsive="xl"
                      size="sm"
                      isEmpty={_.isEmpty(sortedReportData)}
                    >
                      <thead>
                        <tr>
                          <th>{t('drivers.name')}</th>
                          <th>{t('drivers.driven_vehicles')}</th>
                          <th>{t('drivers.orders_count')}</th>
                          <th>{t('carriers.one')}</th>
                          <th>{t('scoreCard.harsh_braking_incidents_points')}</th>
                          <th>{t('scoreCard.harsh_acceleration_incidents_points')}</th>
                          <th>{t('scoreCard.over_speeding_incidents_points')}</th>
                          <th>{t('scoreCard.exceeding_max_driving_hours_points')}</th>
                          <th>{t('scoreCard.below_minimum_rest_hours_points')}</th>
                          <th>{t('scoreCard.driving_distance')}</th>
                          <th
                            onClick={this.handleDrivingScoreSort}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            {t('scoreCard.driving_score')}
                            {' '}
                            {this.getSortIcon()}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(sortedReportData, (scoreData) => (
                          <ReportDriversScoreCardRow
                            key={`itemDataRow-${scoreData.driver_id}-${scoreData.driving_score}`}
                            item={scoreData}
                          />
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </>
        </ActionCard>
      </>
    );
  }
}

ReportDriversScoreCard.propTypes = {
  t: PropTypes.func.isRequired,
  httpRequest: PropTypes.func.isRequired,
};

export default compose(
  withTranslation(),
  withAPI,
  withRouter,
)(ReportDriversScoreCard);
